import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Gem, TrendingUp, ShoppingBag, Share2, Users, Swords, Gift, Award } from 'lucide-react';
import { api, compact, money, timeAgo } from '../lib/api';
import { useAuth } from '../lib/store';
import { Avatar, TierBadge, SkeletonList, SectionHead, Empty } from '../components/ui';

const KIND = {
  purchase: { i: ShoppingBag, c: 'bg-mint-500', l: 'Purchase' },
  referral: { i: Users, c: 'bg-grape-500', l: 'Referral' },
  share: { i: Share2, c: 'bg-blue-500', l: 'Share' },
  rating: { i: Award, c: 'bg-amber-500', l: 'Rating' },
  fight: { i: Swords, c: 'bg-red-500', l: 'Duel' },
  daily: { i: Gift, c: 'bg-pink-500', l: 'Daily' },
  signup: { i: Gift, c: 'bg-brand-500', l: 'Welcome' },
  spend: { i: TrendingUp, c: 'bg-ink-500', l: 'Spent' },
  admin: { i: Award, c: 'bg-ink-700', l: 'Admin' },
  quest: { i: Award, c: 'bg-indigo-500', l: 'Quest' },
};

export default function Wallet() {
  const { user } = useAuth();
  const { data: tx } = useQuery({ queryKey: ['pointsHistory'], queryFn: () => api.get('/purchases/points-history').then(r => r.data) });
  const { data: loyalty } = useQuery({ queryKey: ['myLoyalty'], queryFn: () => api.get('/purchases/my-loyalty').then(r => r.data) });
  const { data: purchases } = useQuery({ queryKey: ['myPurchases'], queryFn: () => api.get('/purchases/mine').then(r => r.data) });

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card p-6 bg-gradient-to-br from-brand-500 via-brand-600 to-grape-600 text-white text-center">
        <Gem size={26} className="mx-auto mb-2 opacity-80" />
        <p className="text-xs font-black uppercase tracking-widest opacity-75">Spendable balance</p>
        <p className="font-display text-5xl font-extrabold my-1">{compact(user?.points_balance)}</p>
        <p className="text-sm font-bold opacity-80">{compact(user?.points_total)} earned all-time</p>
        <div className="grid grid-cols-2 gap-2 mt-5">
          <Link to="/hero" className="btn bg-white text-brand-700 btn-sm font-extrabold">Upgrade hero</Link>
          <Link to="/referrals" className="btn bg-white/20 text-white btn-sm font-extrabold">Earn more</Link>
        </div>
      </div>

      {loyalty?.length > 0 && (
        <div>
          <SectionHead title="Your multipliers" sub="Buy more, earn more per leu" />
          <div className="card p-2 space-y-1">
            {loyalty.map(l => (
              <Link key={l.shop_id} to={`/shop/${l.shop_slug}`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
                <Avatar src={l.logo_url} name={l.shop_name} size={38} square />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{l.shop_name}</p>
                  <p className="text-[11px] text-ink-400 font-semibold">
                    {money(l.total_spent)} · {l.purchases_count} orders
                  </p>
                  {l.next_tier && (
                    <div className="stat-bar mt-1 max-w-[160px]">
                      <div className="stat-fill bg-brand-500"
                        style={{ width: `${Math.min(100, (Number(l.total_spent) / l.next_tier.min_spent) * 100)}%` }} />
                    </div>)}
                </div>
                <TierBadge tier={l.tier} label={l.tier_label} multiplier={l.multiplier} />
              </Link>))}
          </div>
        </div>
      )}

      <div>
        <SectionHead title="Points history" />
        {!tx ? <SkeletonList n={6} h="h-14" />
          : !tx.length ? <Empty icon={Gem} title="No transactions yet" />
          : <div className="card p-2 space-y-1">
            {tx.map(t => {
              const k = KIND[t.kind] || KIND.admin;
              const meta = typeof t.meta === 'string' ? JSON.parse(t.meta || '{}') : (t.meta || {});
              return (
                <div key={t.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
                  <div className={`w-9 h-9 rounded-xl ${k.c} grid place-items-center text-white shrink-0`}>
                    <k.i size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{k.l}{meta.shop && ` · ${meta.shop}`}</p>
                    <p className="text-[11px] text-ink-400 font-semibold">
                      {timeAgo(t.created_at)}{meta.multiplier && ` · ${Number(meta.multiplier).toFixed(2)}×`}
                      {meta.streak && ` · day ${meta.streak}`}
                    </p>
                  </div>
                  <span className={`font-black text-sm ${t.amount > 0 ? 'text-mint-600' : 'text-ink-400'}`}>
                    {t.amount > 0 ? '+' : ''}{compact(t.amount)}
                  </span>
                </div>);
            })}
          </div>}
      </div>

      {purchases?.length > 0 && (
        <div>
          <SectionHead title="Your purchases" />
          <div className="card p-2 space-y-1">
            {purchases.slice(0, 15).map(p => (
              <Link key={p.id} to={`/shop/${p.shop_slug}`} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
                <Avatar src={p.shop_logo} name={p.shop_name} size={34} square />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{p.shop_name}</p>
                  <p className="text-[11px] text-ink-400 font-semibold">{timeAgo(p.created_at)} · {p.multiplier_applied}×</p>
                </div>
                <div className="text-right">
                  <p className="font-black text-sm">{money(p.amount, p.currency)}</p>
                  <p className="text-[11px] text-brand-600 font-black">+{compact(p.total_points)}💎</p>
                </div>
              </Link>))}
          </div>
        </div>
      )}
    </div>
  );
}